import { API_ORIGIN } from '@/constants'

export type CaptchaPublicConfig = {
  enabled: boolean
  misconfigured?: boolean
  provider: string
  siteKey: string
  actions: Record<string, boolean>
  widget: {
    size?: string
    theme?: string
  }
}

export type CaptchaConfigLoadResult = {
  config: CaptchaPublicConfig
  fetchFailed: boolean
}

const DEFAULT_CONFIG: CaptchaPublicConfig = {
  enabled: false,
  misconfigured: false,
  provider: 'hcaptcha',
  siteKey: '',
  actions: {
    registration: true,
    invitation_accept: false,
  },
  widget: {
    size: 'normal',
    theme: 'light',
  },
}

const CAPTCHA_CONFIG_URL = API_ORIGIN
  ? `${API_ORIGIN}/api/accounts/captcha/config`
  : '/api/accounts/captcha/config'

let cachedResult: CaptchaConfigLoadResult | null = null
let inflight: Promise<CaptchaConfigLoadResult> | null = null

export function clearCaptchaConfigCache(): void {
  cachedResult = null
  inflight = null
}

export async function fetchCaptchaConfig(options?: { force?: boolean }): Promise<CaptchaConfigLoadResult> {
  if (!options?.force && cachedResult) {
    return cachedResult
  }
  if (inflight) {
    return inflight
  }

  inflight = (async () => {
    const previous = cachedResult
    try {
      const response = await fetch(CAPTCHA_CONFIG_URL, { method: 'GET' })
      if (!response.ok) {
        const result: CaptchaConfigLoadResult = {
          config: previous?.config ?? DEFAULT_CONFIG,
          fetchFailed: !previous,
        }
        if (!previous) {
          cachedResult = result
        }
        return result
      }
      const data = (await response.json()) as CaptchaPublicConfig
      const result: CaptchaConfigLoadResult = {
        config: {
          ...DEFAULT_CONFIG,
          ...data,
          actions: { ...DEFAULT_CONFIG.actions, ...(data.actions ?? {}) },
          widget: { ...DEFAULT_CONFIG.widget, ...(data.widget ?? {}) },
        },
        fetchFailed: false,
      }
      cachedResult = result
      return result
    } catch {
      const result: CaptchaConfigLoadResult = {
        config: previous?.config ?? DEFAULT_CONFIG,
        fetchFailed: !previous,
      }
      if (!previous) {
        cachedResult = result
      }
      return result
    } finally {
      inflight = null
    }
  })()

  return inflight
}

export function captchaRequiredForAction(
  config: CaptchaPublicConfig | null | undefined,
  action: string,
): boolean {
  if (!config?.enabled) return false
  return Boolean(config.actions?.[action])
}
